import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Fuse from "fuse.js";
import ReactGA from "react-ga4";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import PdfModal from "../../components/pdf/PdfModal";
import useIsMountedRef from "../../utils/mountedRef";
import { DEACTIVATED, DMTG, OTHERS } from "../../views/Home";
import LesDeboursPdf from "../../medias/fiches/Les_debours.pdf";
import SpecificitesAlsaceMosellePdf from "../../medias/fiches/Specificites_alsace_moselle.pdf";
import LesTarifsBaloPdf from "../../medias/fiches/Les_tarifs_BALO.pdf";
import LesTarifsBodaccPdf from "../../medias/fiches/Les_tarifs_BODACC.pdf";
import DispositifLcbFt from "../../medias/fiches/Dispositif LCB-FT.pdf";
import FiscaliteSaferPdf from "../../medias/fiches/Fiscalite_SAFER.pdf";
import CheckListPlusValuesPdf from "../../medias/fiches/Check_list_plus_values.pdf";
import CheckListVentePdf from "../../medias/fiches/Ce_qu_il_ne_faut_pas_oublier_lors_d_une_vente_(check-list).pdf";
import DiagnosticImmobilierPdf from "../../medias/fiches/Diagnostic_immobilier_le_dossier_de_diagnostic_technique_(DDT).pdf";
import BailProfessionnelCommercialPdf from "../../medias/fiches/Le_bail_professionnel_et_le_bail_commercial.pdf";
import FiscaliteImmobilierePdf from "../../medias/fiches/La_fiscalite_immobiliere_quel_taux_et_quel_droit_appliquer.pdf";
import LocationParticuliersPdf from "../../medias/fiches/La_location_pour_les_particuliers_quel_bail.pdf";
import DmtoEtPrimoAccedantsPdf from "../../medias/fiches/DMTO et primo-accédants.pdf";
import CheckListSuccessionPdf from "../../medias/fiches/Ce_qu_il_ne_faut_pas_oublier_lors_d_une_succession_(check-list).pdf";
import DroitsDonationsSuccessionsPdf from "../../medias/fiches/Rappel_des_droits_applicables_aux_successions_et_aux_donations.pdf";
import ExonerationsSuccessionsPdf from "../../medias/fiches/Synthese_des_exonerations_et_abattements_sur_les_successions.pdf";
import ExonerationsDonationsPdf from "../../medias/fiches/Synthese_des_exonerations_et_abattements_sur_les_donations.pdf";
import AssuranceVieSuccessionPdf from "../../medias/fiches/Fiscalite_de_l_assurance_vie_droits_de_succession.pdf";
import UsufruitNueProprietePdf from "../../medias/fiches/Rappel_sur_les_regles_de_repartition_d_usufruit_et_nue-propriete.pdf";
import DevolutionSuccessoralePdf from "../../medias/fiches/Dévolution successorale.pdf";
import ExpertLiquidationCsiPdf from "../../medias/avis/liquidation_CSI_dans_donation_demembree_bien_immobilier.pdf";
import ExpertPmaAmpPdf from "../../medias/avis/pma_ou_amp.pdf";
import ExpertParticipationAcquetsPdf from "../../medias/avis/liquidation_participation_aux_acquets.pdf";
import CdeGuenifey1Pdf from "../../medias/avis/CDE Guénifey 1 SD.pdf";
import CdeGuenifey2Pdf from "../../medias/avis/CDE Guénifey 2 SD.pdf";
import CdeGuenifey3Pdf from "../../medias/avis/CDE Guénifey 3 SD.pdf";
import CdeGuenifey4Pdf from "../../medias/avis/CDE Guénifey 4 SD.pdf";
import Cde01Pdf from "../../medias/avis/CDE 01 pour SD.pdf";
import Cde02Pdf from "../../medias/avis/CDE 02 pour SD.pdf"
import Cde03Pdf from "../../medias/avis/CDE 03 pour SD.pdf";
import Cde04Pdf from "../../medias/avis/CDE 04 pour SD.pdf";
import Cde05Pdf from "../../medias/avis/CDE 05 pour SD.pdf";
import Cde06Pdf from "../../medias/avis/CDE 06 pour SD.pdf";
import Cde07Pdf from "../../medias/avis/CDE 07 pour SD.pdf";
import EmolumentsDeFormalitesPdf from "../../medias/avis/emoluments_de_formalites_principes_generaux_et_zoom.pdf";
import LicitationsRappelPdf from "../../medias/avis/Licitations_rappel_principes_applicables_cas_particuliers.pdf"
import ReformeDroitDesSuretesPdf from "../../medias/avis/reforme_droit_des_suretes_et_frais_actes_notaries.pdf";
import TranslationHypothequePdf from "../../medias/avis/translation_hypotheque.pdf";

const SEARCH_RESULTS_STEP = 5;
const DEFAULT_SEARCH_VISIBLE_COUNTS = {
  simulateurs: SEARCH_RESULTS_STEP,
  fiches: SEARCH_RESULTS_STEP,
  experts: SEARCH_RESULTS_STEP,
  categories: SEARCH_RESULTS_STEP,
  emoluments: SEARCH_RESULTS_STEP,
  formalites: SEARCH_RESULTS_STEP,
  fiscalite: SEARCH_RESULTS_STEP,
  droitsFixes: SEARCH_RESULTS_STEP,
};

const SEARCH_EVENT = {
  category: "Barre de recherche",
  action: "Clic sur barre de recherche",
  label: "Clic sur barre de recherche",
};

const SIMULATEUR_76_SEARCH_RESULTS = [
  {
    name: "Changement de régime matrimonial avec apport",
    searchPathSuffix: "",
    searchKey: "76-changement-regime",
  },
  {
    name: "Contrat de mariage avec apport",
    searchPathSuffix: "#bis",
    searchKey: "76-contrat-mariage",
  },
];

const formatResultLabel = (name, limit = 45) => (
  name.length > limit ? `${name.slice(0, limit)}...` : name
);

const hasAuthenticatedUser = () => {
  try {
    return Boolean(JSON.parse(localStorage.getItem("user"))?.jwt);
  } catch {
    return false;
  }
};

const isSearchableSimulateur = (simulateur) => {
  const simulateurId = Number(simulateur?.id);

  return Boolean(simulateurId) && !DEACTIVATED.includes(simulateurId);
};

const HeaderSearch = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();
  const ctnSearchBarRef = useRef(null);
  const inputRef = useRef(null);

  const [searchValue, setSearchValue] = useState("");
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 1180 : false
  );
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [simulateurs, setSimulateurs] = useState([]);
  const [fiches, setFiches] = useState([]);
  const [expertOpinions, setExpertOpinions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [emolActes, setEmolActes] = useState([]);
  const [emolFormalites, setEmolFormalites] = useState([]);
  const [fiscaActes, setFiscaActes] = useState([]);
  const [droitsFixesActes, setDroitsFixesActes] = useState([]);
  const [searchVisibleCounts, setSearchVisibleCounts] = useState(DEFAULT_SEARCH_VISIBLE_COUNTS);
  const [pdfModal, setPdfModal] = useState(null);

  const normalizedSearchValue = searchValue.trim().toLowerCase();

  const navigateTo = (path) => {
    setSearchValue("");
    history.push(path);
  };

  const handleApiError = (error) => {
    if (error.response?.status === 401 && error.response.data != null) {
      if (error.response.data.error === "Other_user_connected") {
        localStorage.removeItem("user");
        localStorage.setItem("other_user_connected", "other_user_connected");
        window.location.href = "/login";
      }
    }
  };

  const fetchApiData = (url, setter) => {
    if (!hasAuthenticatedUser()) {
      return;
    }

    axios({
      method: "get",
      url,
      headers: {
        "Content-Type": "application/json",
        Authorization: JSON.parse(localStorage.getItem("user"))?.jwt,
      },
    })
      .then((response) => {
        if (response.status === 200 && response.data != null) {
          if (isMountedRef.current) {
            setter(response.data);
          }
        } else {
          enqueueSnackbar("Une erreur est survenue", {
            variant: "error",
          });
        }
      })
      .catch(handleApiError);
  };

  useEffect(() => {
    if (!hasAuthenticatedUser()) {
      return;
    }

    fetchApiData(`${API_BASE_URL}/simulateurs`, setSimulateurs);
    fetchApiData(`${API_BASE_URL}/categories`, setCategories);
    fetchApiData(`${API_BASE_URL}/essentiel_emol`, setEmolActes);
    fetchApiData(`${API_BASE_URL}/emol_form`, setEmolFormalites);
    fetchApiData(`${API_BASE_URL}/essentiel_fiscalite`, setFiscaActes);
    fetchApiData(`${API_BASE_URL}/essentiel_droits_fixes`, setDroitsFixesActes);
    setFiches([
      {
        href: LesDeboursPdf,
        name: "Les débours",
      },
      {
        href: SpecificitesAlsaceMosellePdf,
        name: "Les spécificités des frais d'actes notariés en Alsace-Moselle",
      },
      {
        href: LesTarifsBaloPdf,
        name: "Les tarifs BALO",
      },
      {
        href: LesTarifsBodaccPdf,
        name: "Les tarifs BODACC",
      },
      {
        href: DispositifLcbFt,
        name: "Dispositif LCB-FT",
      },
      {
        href: FiscaliteSaferPdf,
        name: "La fiscalité des SAFER",
      },
      {
        href: CheckListPlusValuesPdf,
        name: "Check-list sur les plus-values",
      },
      {
        href: CheckListVentePdf,
        name: "Check-list lors d'une vente",
      },
      {
        href: DiagnosticImmobilierPdf,
        name: "Les diagnostics immobiliers",
      },
      {
        href: BailProfessionnelCommercialPdf,
        name: "Le bail professionnel et le bail commercial",
      },
      {
        href: FiscaliteImmobilierePdf,
        name: "La fiscalité immobilière, quel taux et quel droit appliquer ?",
      },
      {
        href: LocationParticuliersPdf,
        name: "La location pour les particuliers, quel bail ?",
      },
      {
        href: DmtoEtPrimoAccedantsPdf,
        name: "DMTO ET PRIMO-ACCEDANTS",
      },
      {
        href: CheckListSuccessionPdf,
        name: "Check-list lors de l’ouverture d’une succession",
      },
      {
        href: DroitsDonationsSuccessionsPdf,
        name: "Droits applicables aux donations et successions",
      },
      {
        href: ExonerationsSuccessionsPdf,
        name: "Exonérations et abattements sur les successions",
      },
      {
        href: ExonerationsDonationsPdf,
        name: "Exonérations et abattements sur les donations",
      },
      {
        href: AssuranceVieSuccessionPdf,
        name: "La fiscalité applicable au contrat d’assurance-vie dénoué",
      },
      {
        href: UsufruitNueProprietePdf,
        name: "Barème de l’usufruit (CGI, art. 669)",
      },
      {
        href: DevolutionSuccessoralePdf,
        name: "Dévolution successorale",
      },
    ]);
    setExpertOpinions([
      {
        href: CdeGuenifey4Pdf,
        name: "Comment le rapport successoral doit-il s’exercer lorsque la donation d’argent donnée a permis la souscription de titres de société ?"
      },
      {
        href: Cde07Pdf,
        name: "Lorsque deux acquéreurs deviennent propriétaires de leur résidence principale en indivision ou pour le compte de la communauté existant entre eux, dans quelles hypothèses est-il possible de ne pas appliquer la hausse du taux de droits de mutation à titre onéreux ?"
      },
      {
        href: CdeGuenifey3Pdf,
        name: "Lorsqu’un bien reçu par succession est vendu, comment déterminer le prix d’acquisition ?"
      },
      {
        href: Cde06Pdf,
        name: "Lorsqu'un héritier renonçant a reçu une donation dans les 15 ans précédant le décès du donateur, faut il en tenir compte pour calculer les droits dus de succession par ses représentants?"
      },
      {
        href: Cde05Pdf,
        name: "Lorsqu'un héritier a recelé des actifs successoraux et que la créance correspondante est irrécouvrable, doit-elle être incluse dans l'assiette des émoluments du notaire afférents à l'acte de partage ?"
      },
      {
        href: Cde03Pdf,
        name: "Lorsqu'un donataire est frappé d'un handicap l'empêchant de travailler dans des conditions normales de rentabilité, l'application de l'abattement de 159 325 euros peut-elle se présumer ou s'effectuer par priorité à l'abattement en ligne directe ?"
      },
      {
        href: Cde02Pdf,
        name: "L'industrie personnelle d'un époux entre-t-elle dans le cadre de sa contribution aux charges du mariage lorsque celui-ci a réalisé des travaux logement familial ?"
      },
      {
        href: Cde01Pdf,
        name: "Lorsqu'une déclaration de succession, devant être déposée en France, inclut des éléments d'actif situés à l'étranger faisant par ailleurs l'objet d'une déclaration fiscale dans un autre pays, l'émolument doit-il avoir pour base le patrimoine mondial ou le seul patrimoine français ?"
      },
      {
        href: CdeGuenifey2Pdf,
        name: "La renonciation à succession par le conjoint survivant entraîne-t-elle également renonciation au bénéfice d'une donation entre époux ?"
      },
      {
        href: CdeGuenifey1Pdf,
        name: "Qui doit recevoir les capitaux-décès d'assurance-vie lorsque le bénéficiaire décède avant d'avoir accepté le bénéfice du contrat à son profit ?"
      },
      {
        href: ExpertLiquidationCsiPdf,
        name: "La liquidation de la CSI dans une donation démembrée d’un bien immobilier",
      },
      {
        href: ExpertPmaAmpPdf,
        name: "La procréation médicalement assistée (PMA) ou assistance médicale à la procréation (AMP) : quelle taxe pour quel acte ?",
      },
      {
        href: ExpertParticipationAcquetsPdf,
        name: "Liquidation d’une participation aux acquêts : principes applicables à l’émolument d’acte",
      },
      {
        href: EmolumentsDeFormalitesPdf,
        name: "Les Émoluments de Formalités : Principes Généraux et Zoom Sur le N° 204 du Tableau 5",
      },
      {
        href: Cde04Pdf,
        name: "Lorsqu'un bien était achevé depuis moins de 5 ans au jour du compromis de vente mais l'est depuis plus de 5 ans au jour de la réitération, la vente entre-t-elle ou non dans le champ de la TVA immobilière ?"
      },
      {
        href: LicitationsRappelPdf,
        name: "Les Licitations : Rappel des Principes Applicables et Cas Particuliers",
      },
      {
        href: ReformeDroitDesSuretesPdf,
        name: "La Réforme du Droit des Sûretés et les Frais d'Actes Notariés",
      },
      {
        href: TranslationHypothequePdf,
        name: "La Translation d'Hypothèque",
      }
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMountedRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ctnSearchBarRef.current && !ctnSearchBarRef.current.contains(event.target)) {
        setSearchValue("");
        setIsMobileSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setSearchVisibleCounts({ ...DEFAULT_SEARCH_VISIBLE_COUNTS });
  }, [normalizedSearchValue]);
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleResize = () => {
      const isMobile = window.innerWidth <= 1180;
      setIsMobileViewport(isMobile);
      if (!isMobile) {
        setIsMobileSearchOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobileViewport && isMobileSearchOpen) {
      inputRef.current?.focus();
    }
  }, [isMobileSearchOpen, isMobileViewport]);

  const formatSearchActeName = (acte) => {
    const normalizedActe = `${acte.substring(0, 1).toUpperCase()}${acte.substring(1).toLowerCase()}`;
    return normalizedActe.length > 40 ? `${normalizedActe.slice(0, 40)}...` : normalizedActe;
  };

  const trackSearchClick = () => {
    ReactGA.event(SEARCH_EVENT);
  };

  const openPdfModal = (pdfUrl, variant) => {
    setPdfModal({ pdfUrl, variant });
    setSearchValue("");
    setIsMobileSearchOpen(false);
    trackSearchClick();
  };

  const closePdfModal = () => {
    setPdfModal(null);
  };

  const buildLinkResult = (keyPrefix, item, index, onClick) => {
    const isFirstItem = index === 0;

    return (
      <div
        key={`${keyPrefix}-${item.href || item.searchKey || item.id}`}
        className="itemProposal"
        aria-label={item.name}
        onClick={onClick}
      >
        {isFirstItem ? (
          <div className="firstItemText">{formatResultLabel(item.name)}</div>
        ) : (
          formatResultLabel(item.name)
        )}
      </div>
    );
  };

  const buildActeResult = (keyPrefix, item, hashIndex) => (
    <div
      key={`${keyPrefix}-${item.id}-${item.n_acte}`}
      className="itemProposal"
      aria-label={item.actes}
      onClick={() => {
        const target = item.id ? `id:${item.id}` : `acte:${encodeURIComponent(item.n_acte)}`;
        navigateTo(`/tarif_des_notaires#${hashIndex}#${target}`);
        trackSearchClick();
      }}
    >
      {formatSearchActeName(item.actes)}
    </div>
  );

  const buildFormaliteResult = (item) => (
    <div
      key={`formalites-${item.id}`}
      className="itemProposal"
      aria-label={item.formalite}
      onClick={() => {
        navigateTo("/tarif_des_notaires#emolForm");
        trackSearchClick();
      }}
    >
      {formatResultLabel(item.formalite, 55)}
    </div>
  );

  const uniqueFuseResultsByActe = (results) => results.reduce((acc, result) => {
    const acte = result.item.actes;
    if (!acc.some((item) => item.item.actes === acte)) {
      acc.push(result);
    }
    return acc;
  }, []);

  const buildSearchResults = () => {
    if (normalizedSearchValue.length < 2) {
      return {
        simulateursProposal: [],
        fichesProposal: [],
        expertOpinionsProposal: [],
        categoriesProposal: [],
        emolActeProposal: [],
        emolFormaliteProposal: [],
        fiscaActeProposal: [],
        droitsFixesActeProposal: [],
      };
    }

    const maxResultsByGroup = 30;
    const options = {
      keys: ["name"],
      threshold: 0.3,
      ignoreLocation: true,
    };
    const actesOptions = {
      keys: ["actes"],
      threshold: 0.3,
    };
    const formalitesOptions = {
      keys: ["formalite"],
      threshold: 0.3,
    };

    const simulateursForSearch = simulateurs.flatMap((simulateur) => {
      if (Number(simulateur.id) !== 76) {
        return [simulateur];
      }

      return SIMULATEUR_76_SEARCH_RESULTS.map((searchResult) => ({
        ...simulateur,
        ...searchResult,
      }));
    });

    const simulateursProposal = new Fuse(simulateursForSearch, options)
      .search(normalizedSearchValue)
      .filter((result) => isSearchableSimulateur(result.item))
      .slice(0, maxResultsByGroup)
      .map((result, index) => {
        const item = result.item;

        return buildLinkResult("simulateur", item, index, () => {
          const firstSubcategory = item.subcategorie?.[0];
          const categoryUrl = firstSubcategory?.categorie?.url_name || "autres";
          const searchPathSuffix = item.searchPathSuffix || "";

          if (firstSubcategory?.shortname === "DMTG" || DMTG.includes(Number(item.id))) {
            navigateTo(`/categorie/${categoryUrl}/dmtg/${item.id}${searchPathSuffix}`);
          } else if (categoryUrl === "autres" || OTHERS.includes(Number(item.id))) {
            navigateTo(`/categorie/${categoryUrl}/others/${item.id}${searchPathSuffix}`);
          } else {
            navigateTo(`/categorie/${categoryUrl}/simulateur/${item.id}${searchPathSuffix}`);
          }

          trackSearchClick();
        });
      });

    const fichesProposal = new Fuse(fiches, options)
      .search(normalizedSearchValue)
      .slice(0, maxResultsByGroup)
      .map((result, index) =>
        buildLinkResult("fiche", result.item, index, () => openPdfModal(result.item.href, "fiches"))
      );

    const expertOpinionsProposal = new Fuse(expertOpinions, options)
      .search(normalizedSearchValue)
      .slice(0, maxResultsByGroup)
      .map((result, index) =>
        buildLinkResult("expert", result.item, index, () => openPdfModal(result.item.href, "experts"))
      );

    const categoriesProposal = new Fuse(categories, {
      keys: ["categorie_name", "url_name"],
      threshold: 0.3,
    })
      .search(normalizedSearchValue)
      .slice(0, maxResultsByGroup)
      .map((result, index) => buildLinkResult("categorie", {
        id: result.item.categorie_id,
        name: result.item.categorie_name,
      }, index, () => {
        navigateTo(`/categorie/${result.item.url_name}`);
        trackSearchClick();
      }));

    const emolActeProposal = uniqueFuseResultsByActe(
      new Fuse(emolActes, actesOptions).search(normalizedSearchValue).slice(0, maxResultsByGroup)
    ).map((result) => buildActeResult("emol", result.item, 0));

    const emolFormaliteProposal = new Fuse(emolFormalites, formalitesOptions)
      .search(normalizedSearchValue)
      .slice(0, maxResultsByGroup)
      .map((result) => buildFormaliteResult(result.item));

    const fiscaActeProposal = uniqueFuseResultsByActe(
      new Fuse(fiscaActes, actesOptions).search(normalizedSearchValue).slice(0, maxResultsByGroup)
    ).map((result) => buildActeResult("fisca", result.item, 1));

    const droitsFixesActeProposal = uniqueFuseResultsByActe(
      new Fuse(droitsFixesActes, actesOptions).search(normalizedSearchValue).slice(0, maxResultsByGroup)
    ).map((result) => buildActeResult("droits-fixes", result.item, 2));

    return {
      simulateursProposal,
      fichesProposal,
      expertOpinionsProposal,
      categoriesProposal,
      emolActeProposal,
      emolFormaliteProposal,
      fiscaActeProposal,
      droitsFixesActeProposal,
    };
  };

  const {
    simulateursProposal,
    fichesProposal,
    expertOpinionsProposal,
    categoriesProposal,
    emolActeProposal,
    emolFormaliteProposal,
    fiscaActeProposal,
    droitsFixesActeProposal,
  } = buildSearchResults();

  const hasSearchResults =
    simulateursProposal.length > 0 ||
    fichesProposal.length > 0 ||
    expertOpinionsProposal.length > 0 ||
    categoriesProposal.length > 0 ||
    emolActeProposal.length > 0 ||
    emolFormaliteProposal.length > 0 ||
    fiscaActeProposal.length > 0 ||
    droitsFixesActeProposal.length > 0;

  const searchGroups = [
    ["simulateurs", "Simulateurs", simulateursProposal],
    ["categories", "Catégories", categoriesProposal],
    ["fiches", "Fiches pratiques", fichesProposal],
    ["experts", "Avis d'experts", expertOpinionsProposal],
    ["emoluments", "Émoluments d'actes", emolActeProposal],
    ["formalites", "Émoluments de formalités", emolFormaliteProposal],
    ["fiscalite", "Fiscalité", fiscaActeProposal],
    ["droitsFixes", "Droits fixes", droitsFixesActeProposal],
  ];

  const showMoreSearchResults = (key) => {
    setSearchVisibleCounts((previousCounts) => ({
      ...previousCounts,
      [key]: (previousCounts[key] || SEARCH_RESULTS_STEP) + SEARCH_RESULTS_STEP,
    }));
  };

  const renderSearchGroup = (key, title, items) => {
    if (items.length === 0) {
      return null;
    }

    const visibleCount = searchVisibleCounts[key] || SEARCH_RESULTS_STEP;
    const remainingCount = items.length - visibleCount;

    return (
      <div key={key} className="headerSearchGroup">
        <div className="headerSearchGroupTitle">{title}</div>
        {items.slice(0, visibleCount)}
        {remainingCount > 0 ? (
          <button
            type="button"
            className="headerSearchMore"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              showMoreSearchResults(key);
            }}
          >
            Voir plus
            <svg aria-hidden="true" className="headerSearchMoreIcon" viewBox="0 0 24 24">
              <path d="M7 10l5 5 5-5" />
            </svg>
          </button>
        ) : null}
      </div>
    );
  };
  const toggleMobileSearch = () => {
    if (!isMobileViewport) {
      return;
    }

    setIsMobileSearchOpen((currentValue) => {
      const nextValue = !currentValue;
      if (!nextValue) {
        setSearchValue("");
      }
      return nextValue;
    });
  };

  return (
    <>
      <form
        ref={ctnSearchBarRef}
        className={`headerSearch ${isMobileViewport && isMobileSearchOpen ? "is-mobile-open" : ""}`}
        onSubmit={(e) => e.preventDefault()}
      >
      <button
        type="button"
        className="headerSearchToggle"
        aria-label={isMobileSearchOpen ? "Fermer la recherche rapide" : "Ouvrir la recherche rapide"}
        aria-expanded={isMobileSearchOpen}
        onClick={toggleMobileSearch}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 45.3 45.3"
          width={22}
          height={22}
          fill="currentColor"
          aria-hidden="true"
        >
          {isMobileSearchOpen ? (
            <path d="M9.5 9.5l26.3 26.3m0-26.3L9.5 35.8" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" fill="none" />
          ) : (
            <path d="M40.17,5.17C35.98.96,29.94-.85,24.12.37c-5.82,1.22-10.62,5.3-12.77,10.84-2.15,5.54-1.36,11.79,2.11,16.62L1.16,40.14c-.67.49-1.09,1.26-1.15,2.09-.06.83.24,1.64.83,2.23.59.59,1.4.89,2.23.83.83-.06,1.59-.48,2.09-1.15l12.31-12.3c4.82,3.46,11.07,4.26,16.6,2.12,5.54-2.14,9.61-6.94,10.84-12.75,1.23-5.8-.55-11.84-4.74-16.05h0ZM36.82,26.64c-3.66,3.64-9.15,4.72-13.92,2.74-4.77-1.98-7.88-6.64-7.88-11.8s3.11-9.82,7.88-11.8c4.77-1.98,10.26-.9,13.92,2.74,2.41,2.4,3.76,5.66,3.76,9.06s-1.35,6.66-3.76,9.06h0Z" />
          )}
        </svg>
      </button>
      <div className="headerSearchPanel">
        <div className="headerSearchWrapper">
          <span className="headerSearchInputWrapper">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 45.3 45.3"
              width={22}
              height={22}
              fill="currentColor"
              className="headerSearchIcon"
            >
              <path d="M40.17,5.17C35.98.96,29.94-.85,24.12.37c-5.82,1.22-10.62,5.3-12.77,10.84-2.15,5.54-1.36,11.79,2.11,16.62L1.16,40.14c-.67.49-1.09,1.26-1.15,2.09-.06.83.24,1.64.83,2.23.59.59,1.4.89,2.23.83.83-.06,1.59-.48,2.09-1.15l12.31-12.3c4.82,3.46,11.07,4.26,16.6,2.12,5.54-2.14,9.61-6.94,10.84-12.75,1.23-5.8-.55-11.84-4.74-16.05h0ZM36.82,26.64c-3.66,3.64-9.15,4.72-13.92,2.74-4.77-1.98-7.88-6.64-7.88-11.8s3.11-9.82,7.88-11.8c4.77-1.98,10.26-.9,13.92,2.74,2.41,2.4,3.76,5.66,3.76,9.06s-1.35,6.66-3.76,9.06h0Z" />
            </svg>
            <div className="headerSearchContainer">
              <input
                ref={inputRef}
                type="text"
                className="headerSearchInput"
                placeholder="Recherche rapide"
                value={searchValue}
                onFocus={() => {
                  if (isMobileViewport) {
                    setIsMobileSearchOpen(true);
                  }
                }}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              {searchValue ? (
                <button type="button" className="headerSearchClear" onClick={() => setSearchValue("")} aria-label="Effacer la recherche">
                  ✕
                </button>
              ) : null}
            </div>
          </span>
        </div>
        {normalizedSearchValue.length >= 2 && (!isMobileViewport || isMobileSearchOpen) ? (
          <div className="headerSearchProposes">
            {hasSearchResults ? (
              searchGroups.map(([key, title, items]) => renderSearchGroup(key, title, items))
            ) : (
              <div className="headerSearchEmpty">Aucun résultat trouvé</div>
            )}
          </div>
        ) : null}
      </div>
      </form>
      {pdfModal && (
        <PdfModal
          pdfUrl={pdfModal.pdfUrl}
          onClose={closePdfModal}
          variant={pdfModal.variant}
        />
      )}
    </>
  );
};

export default HeaderSearch;
